import {
  Flex,
  Text,
  Button,
  Box,
  Badge,
  chakra,
  Checkbox,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useOutsideClick,
  useTheme,
  useToast,
} from "@chakra-ui/react";
import IssbContainer from "./IssbContainer";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { H2, H4 } from "components/text/headings";
import reportDocService from "api/issb/reportdoc.service";
import { useQuery, useQueryClient } from "react-query";
import { useAuth } from "context";
import PageHeader from "components/text/PageHeader";
import {
  CaretDown,
  Clock,
  DotsThreeVertical,
  Eye,
  MagnifyingGlass,
  Rows,
  SquaresFour,
  Trash,
  User,
  X,
} from "@phosphor-icons/react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { routes } from "config/paths";
import WithAuthorization from "context/Auth/WithAuthorization";
import { formatDate, showSuccessToast } from "utils";
import A from "components/A";
import { Card } from "components/Card";
import SecondaryHeader from "./components/SecondaryHeader";
import LifecycleState from "api/common/LifecycleState";
import { ReportDocStateToggle } from "./ReportDocEditorPanel";
// Using this api for fetching the username
import api from "api/user-administration/user-administration";
import { apiGetThreads, taskService } from "api/ai/ai.service";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";

export function ReportDocHeader() {
  return (
    <PageHeader
      title="Insights and Reports"
      subTitle="AI-Powered sustainability insights and report generation from your vetted disclosures and ESG data"
    />
  );
}

const STATUS_LABELS = {
  [LifecycleState.DRAFT]: "Draft",
  [LifecycleState.ACTIVE]: "Published",
  [LifecycleState.PROCESSING]: "Processing",
  [LifecycleState.ERROR]: "Error",
  [LifecycleState.ARCHIVED]: "Archived",
};

/**
 * ReportDoc.lastModifiedBy is set from the raw JWT `sub` claim on save. When the AI report-writer
 * saves via its machine-to-machine credential, that's "{m2mClientId}@clients" or "system", not a real
 * Auth0 user id — calling getUserById() on it 404s against Auth0's Management API. Detect that case
 * so we can show "AI" instead of attempting (and failing) a user lookup.
 */
function isSystemOrAiUserId(userId) {
  return !userId || userId === "system" || userId.endsWith("@clients");
}

/** Resolves a user id to a display name, short-circuiting AI/system ids to "AI" (see isSystemOrAiUserId). */
function useUserDisplayName(userId) {
  const { data: userName } = useQuery({
    queryKey: ["userName", userId],
    queryFn: () => api.getUserById(userId).then((user) => user.username),
    enabled: !!userId && !isSystemOrAiUserId(userId),
  });
  if (isSystemOrAiUserId(userId)) return "AI";
  return userName;
}

/** Segmented List/Grid pill, mirrors OrganizationPlanning.jsx's view toggle. */
function ViewToggle({ viewType, onChange }) {
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      <Button
        size="sm"
        borderRadius="full"
        bg={viewType === "list" ? "darkTeal" : "transparent"}
        color={viewType === "list" ? "white" : "gray.500"}
        _hover={{ bg: viewType === "list" ? "darkTeal" : "gray.300" }}
        rightIcon={<Rows size={16} />}
        fontWeight="600"
        fontSize="13px"
        px={4}
        onClick={() => onChange("list")}
      >
        List
      </Button>
      <Button
        size="sm"
        borderRadius="full"
        bg={viewType === "grid" ? "darkTeal" : "transparent"}
        color={viewType === "grid" ? "white" : "gray.500"}
        _hover={{ bg: viewType === "grid" ? "darkTeal" : "gray.300" }}
        rightIcon={<SquaresFour size={16} />}
        fontWeight="600"
        fontSize="13px"
        px={4}
        onClick={() => onChange("grid")}
      >
        Grid
      </Button>
    </Flex>
  );
}

/** Generic pill dropdown filter (checkbox list), mirrors TaskFilterBar.jsx's FilterPill. */
function FilterPill({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        {label}
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="250px"
          overflowY="auto"
          py={2}
        >
          <Stack align="start" spacing={0} px={3}>
            {options.map((option) => {
              const isChecked = selected.includes(option.value);
              return (
                <Checkbox
                  key={option.value}
                  size="sm"
                  colorScheme="limeShade"
                  py={1.5}
                  w="100%"
                  isChecked={isChecked}
                  onChange={() =>
                    onChange(
                      isChecked ? selected.filter((v) => v !== option.value) : [...selected, option.value]
                    )
                  }
                >
                  <Text fontSize="13px" color="gray.600">
                    {option.label}
                  </Text>
                </Checkbox>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/** Search + filter pills + clear-all, mirrors TaskFilterBar.jsx's chrome. */
function ReportDocFilterBar({
  searchTerm,
  onSearchChange,
  statusOptions,
  statusFilter,
  onStatusFilterChange,
  authorOptions,
  authorFilter,
  onAuthorFilterChange,
}) {
  const hasSearch = searchTerm?.length > 0;
  const hasAnyFilter = hasSearch || statusFilter.length > 0 || authorFilter.length > 0;

  const handleClearAll = () => {
    onSearchChange("");
    onStatusFilterChange([]);
    onAuthorFilterChange([]);
  };

  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <InputGroup size="sm" maxW="220px">
        <InputLeftElement pointerEvents="none">
          <MagnifyingGlass size={14} color="#A0AEC0" />
        </InputLeftElement>
        <Input
          borderRadius="full"
          placeholder="Search reports..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          borderColor={hasSearch ? "lime" : "gray.200"}
          bg={hasSearch ? "lightLime" : "white"}
          _hover={{ borderColor: "lime" }}
          _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
        />
      </InputGroup>

      <FilterPill label="Status" options={statusOptions} selected={statusFilter} onChange={onStatusFilterChange} />
      <FilterPill label="Created by" options={authorOptions} selected={authorFilter} onChange={onAuthorFilterChange} />

      {hasAnyFilter && (
        <Button
          size="sm"
          variant="ghost"
          color="gray.500"
          fontSize="12px"
          fontWeight="normal"
          leftIcon={<X size={12} />}
          onClick={handleClearAll}
          _hover={{ color: "gray.700" }}
        >
          Clear all
        </Button>
      )}
    </Flex>
  );
}

const ReportDocListPage = () => {
  const roundSelector = useSelector((state) => state.issbRound);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const createReportDocAndGotoEditor = () => {
    // make a stub report doc if none exists, and get an id
    const stubReportDoc = { state: LifecycleState.PROCESSING };
    reportDocService.create(stubReportDoc).then((newReportDoc) => {
      console.log("newReportDoc", newReportDoc.id);
      // To prevent stale processing after navigation takes place.
      /** 
        Manually putting data into React Query’s cache for the specific reportDoc query key, 
        so that when another part of your app asks for it later, it doesn’t get an old/stale value (or an empty cache)
      **/
      queryClient.setQueriesData(["reportDoc", newReportDoc.id], newReportDoc);

      queryClient.invalidateQueries(["reportDocList", roundSelector?.selectedRound]);
      navigate(routes.reportDocEditor.replace(":id", newReportDoc.id));
    });
  };

  return (
    <SecondaryHeader>
      <IssbContainer>
        <ReportDocHeader />
        <Box display="flex" flexDirection="column" gap={4} width="100%">
          <H2>Reports</H2>
          <ReportDocList round={roundSelector?.selectedRound} onCreateNew={createReportDocAndGotoEditor} />
        </Box>
      </IssbContainer>
    </SecondaryHeader>
  );
};

/** "Generate new report" hero tile — mirrors OrganizationPlanning.jsx's "Add Data" promo card
 * (lightLime bg, lime border, decorative corner graphic, pill CTA button). Rendered as the first
 * tile in the grid, top-left, same position as the equivalent card everywhere else in the app. */
function GenerateReportCard({ onClick }) {
  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="lime"
      borderRadius="lg"
      bg="lightLime"
      _hover={{ borderColor: "limeShade.400" }}
      transition="all 0.15s"
      overflow="hidden"
      position="relative"
      px={6}
      pt={5}
      pb={8}
    >
      <chakra.svg
        position="absolute"
        right="0"
        bottom="0"
        width="220px"
        height="180px"
        viewBox="0 0 175 114"
        preserveAspectRatio="xMaxYMax meet"
        fill="none"
        pointerEvents="none"
      >
        <path
          d="M31.2441 113.272C31.2066 113.31 31.1694 113.349 31.1318 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z"
          fill="#85C22A"
          fillOpacity="0.15"
        />
        <path
          d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z"
          fill="#85C22A"
          fillOpacity="0.15"
        />
        <path
          d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z"
          fill="#85C22A"
          fillOpacity="0.15"
        />
      </chakra.svg>
      <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>
        Generate new report
      </Text>
      <Text fontSize="14px" color="gray.600" mb={5} maxW="70%" position="relative" zIndex={1}>
        Our AI will help you create a tailored report for your stakeholders.
      </Text>
      <Flex gap={2} position="relative" zIndex={1}>
        <Button bg="limeShade.600" color="white" borderRadius="full" _hover={{ bg: "limeShade.700" }} size="sm" onClick={onClick}>
          Generate new report
        </Button>
      </Flex>
    </Box>
  );
}

function ReportDocList({ round, onCreateNew }) {
  const { getAccessTokenSilently } = useAuth();

  const reportDocQueryClient = useQueryClient();

  const [viewType, setViewType] = useState(() => sessionStorage.getItem("reportDocViewType") || "grid");
  const handleSetViewType = (type) => {
    setViewType(type);
    sessionStorage.setItem("reportDocViewType", type);
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [authorFilter, setAuthorFilter] = useState([]);

  // TODO filter by round.id in sourcesAllowed
  const {
    data: reportDocsPage,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["reportDocList", round],
    queryFn: () =>
      getAccessTokenSilently().then(() =>
        reportDocService.list({
          sort: "lastModifiedDate,desc",
        }, "/summary?size=50")
      ),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });
  // To run on every page load everytime everywhere.
  useEffect(() => {
    reportDocQueryClient.invalidateQueries({ queryKey: ["reportDocList", round] });
  }, [reportDocQueryClient, round]);


  // let reportDocs = reportDocsPage?.content || [];
  // reportDocs = reportDocs.filter((reportDoc) => reportDoc.content || reportDoc.title); // filter out blanks, since we make a stub at the start

  const reportDocs = useMemo(() => {
    const list = reportDocsPage?.content ?? [];
    // filter out blanks, since we make a stub at the start
    return list.filter((d) => d.content || d.title);
  }, [reportDocsPage?.content]);


  /*
    Issue being faced here : ist query (reportDocService.list) refreshes every 5s and shows the correct latest state (e.g. DRAFT).
    But the per-doc query (useQuery(["reportDoc", id], ...) on the editor page might still hold an old cached version (e.g. still PROCESSING)
    This is why even if the List card shows DRAFT, when I click it, it shows me the page meant for PROCESSING state.

    Solution here: Use useEffect hook to walk through the freshly processed lists and push each row into per-doc cache.
  */

  // Sync each doc's individual cache with the fresher list row
  useEffect(() => {
    if (!reportDocs?.length) return;
    for (const report of reportDocs) {
      reportDocQueryClient.setQueryData(["reportDoc", report.id], (prev) => {
        if (!prev) return report;
        // only replace if newer
        return prev.lastModifiedDate < report.lastModifiedDate ? report : prev;
      });
    }
  }, [reportDocQueryClient, reportDocs]);

  // Resolve + cache display names for every doc's createdBy — shares the ["userName", id]
  // cache key with ReportDocCard, so this doesn't cause duplicate fetches. createdBy is always
  // set from the human user's own JWT (see createReportDocAndGotoEditor), so no AI/system guard
  // is needed here (unlike lastModifiedBy, which we deliberately don't show — see isSystemOrAiUserId).
  const userIds = useMemo(() => [...new Set(reportDocs.map((d) => d.createdBy).filter(Boolean))], [reportDocs]);
  const { data: userNamesById = {} } = useQuery({
    queryKey: ["reportDocUserNames", userIds],
    queryFn: async () => {
      const entries = await Promise.all(
        userIds.map(async (id) => {
          const cached = reportDocQueryClient.getQueryData(["userName", id]);
          if (cached) return [id, cached];
          const user = await api.getUserById(id);
          reportDocQueryClient.setQueryData(["userName", id], user.username);
          return [id, user.username];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: userIds.length > 0,
  });
  const displayNameFor = useCallback((userId) => userNamesById[userId], [userNamesById]);

  const statusOptions = useMemo(() => {
    const statuses = [...new Set(reportDocs.map((d) => d.state).filter(Boolean))];
    return statuses.map((state) => ({ label: STATUS_LABELS[state] || state, value: state }));
  }, [reportDocs]);

  const authorOptions = useMemo(() => {
    const names = [...new Set(reportDocs.map((d) => displayNameFor(d.createdBy)).filter(Boolean))];
    return names.sort().map((name) => ({ label: name, value: name }));
  }, [reportDocs, displayNameFor]);

  const filteredReportDocs = useMemo(() => {
    let list = reportDocs;
    // Archived reports are hidden by default — only shown when the user explicitly selects
    // "Archived" in the Status filter, so they don't clutter the standard card/list view.
    if (!statusFilter.includes(LifecycleState.ARCHIVED)) {
      list = list.filter((d) => d.state !== LifecycleState.ARCHIVED);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((d) => {
        const createdByName = (displayNameFor(d.createdBy) || "").toLowerCase();
        return (d.title || "").toLowerCase().includes(term) || createdByName.includes(term);
      });
    }
    if (statusFilter.length > 0) {
      list = list.filter((d) => statusFilter.includes(d.state));
    }
    if (authorFilter.length > 0) {
      list = list.filter((d) => authorFilter.includes(displayNameFor(d.createdBy)));
    }
    return list;
  }, [reportDocs, searchTerm, statusFilter, authorFilter, displayNameFor]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <Stack spacing={4}>
      <ReportDocFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusOptions={statusOptions}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        authorOptions={authorOptions}
        authorFilter={authorFilter}
        onAuthorFilterChange={setAuthorFilter}
      />

      <Flex justify="space-between" align="flex-start">
        <Flex direction="column" align="flex-start" gap={3}>
          <Text fontSize="18px" fontWeight="700" color="gray.600">
            {filteredReportDocs.length} {filteredReportDocs.length === 1 ? "report" : "reports"}
            {filteredReportDocs.length !== reportDocs.length && ` of ${reportDocs.length}`}
          </Text>
          {viewType === "list" && (
            <Button bg="limeShade.600" color="white" borderRadius="full" _hover={{ bg: "limeShade.700" }} size="sm" onClick={onCreateNew}>
              Generate new report
            </Button>
          )}
        </Flex>
        <ViewToggle viewType={viewType} onChange={handleSetViewType} />
      </Flex>

      {viewType === "list" ? (
        <ReportDocTable reportDocs={filteredReportDocs} displayNameFor={displayNameFor} />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <GenerateReportCard onClick={onCreateNew} />
          {filteredReportDocs.map((reportDoc) => (
            <ReportDocCard key={reportDoc.id} reportDoc={reportDoc} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}

/** Fetches task/thread progress for a PROCESSING reportDoc — shared by card and list row. */
function useReportDocProgress(reportDoc) {
  const { data: task } = useQuery({
    queryKey: ["task", reportDoc.taskId],
    queryFn: () => {
      return taskService.get(reportDoc.taskId)
    },
    onSuccess: (data) => {
      if (data.status === "failed") {
        console.log("Report doc task=failed", "task", data, "reportDoc", reportDoc);
      }
    },
    enabled: !!(reportDoc.state === LifecycleState.PROCESSING && reportDoc.taskId)
  });
  const { data: thread } = useQuery({
    queryKey: ["thread", reportDoc.id],
    queryFn: () => apiGetThreads({ reportDocId: reportDoc.id }).then((threads) => threads[0]),
    enabled: !!(reportDoc.state === LifecycleState.PROCESSING),
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });
  return { task, thread };
}

/** Soft-delete (archive) action for a reportDoc — shared by card and table row. */
function useReportDocActions(reportDoc) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { confirm } = useConfirmation();

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Report " + (reportDoc?.title || ""),
      message: "Are you sure you want to delete this report?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    reportDocService.update(reportDoc.id, { ...reportDoc, state: LifecycleState.ARCHIVED }).then(() => {
      showSuccessToast(toast, "Report doc deleted", "report-doc-deleted");
      queryClient.invalidateQueries({ queryKey: ["reportDocList"] });
    });
  };

  return { handleDelete };
}

/** Open/Delete kebab menu — shared by card and table row. */
function ReportDocActionsMenu({ reportDoc, href, iconButtonProps }) {
  const navigate = useNavigate();
  const { handleDelete } = useReportDocActions(reportDoc);

  return (
    <Menu isLazy>
      <MenuButton
        as={IconButton}
        aria-label="Actions"
        icon={<DotsThreeVertical size={20} weight="bold" />}
        variant="ghost"
        size="sm"
        borderRadius="full"
        {...iconButtonProps}
      />
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="160px">
        <MenuItem icon={<Eye size={16} />} onClick={() => navigate(href)} fontSize="sm" color="gray.700">
          Open
        </MenuItem>
        <MenuItem icon={<Trash size={16} color="#E53E3E" />} onClick={handleDelete} fontSize="sm" color="red.500">
          Delete
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

function ReportDocCard({ reportDoc }) {
  // phosphor icon does not support color name like gray.500
  const { colors } = useTheme();

  const createdByName = useUserDisplayName(reportDoc.createdBy);
  const { task, thread } = useReportDocProgress(reportDoc);
  const href = routes.reportDocEditor.replace(":id", reportDoc.id);

  return (
    <Card
      opacity={1}
      w="100%"
      color="teal"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      boxShadow="sm"
      bg="gray.50"
      position="relative"
    >
      <Box position="absolute" top={2} right={2}>
        <ReportDocActionsMenu reportDoc={reportDoc} href={href} />
      </Box>
      <A href={href}>
        <Box display="flex" flexDirection="column" gap={2} fontWeight="normal" maxHeight="100%">
          <ReportDocStateToggle reportDoc={reportDoc} task={task} thread={thread} />
          <H4 fontSize="heading.s" pr={8}>{reportDoc.title}</H4>
          <Box display="flex" flexDirection="row" gap={2} align="center">
            <User size={12} color={colors.gray[500]} />
            <Text fontSize="body.xs" color="gray.500">
              Created by {createdByName} on {formatDate(reportDoc.createdDate)}
            </Text>
          </Box>
          <Box display="flex" flexDirection="row" gap={2} align="center">
            <Clock size={12} color={colors.gray[500]} />
            <Text fontSize="body.xs" color="gray.500">
              Last modified {formatDate(reportDoc.lastModifiedDate)}
            </Text>
          </Box>
        </Box>
      </A>
    </Card>
  );
}

const SORT_DIRECTIONS = { NONE: "none", ASC: "asc", DESC: "desc" };

function nextSortDirection(current) {
  switch (current) {
    case SORT_DIRECTIONS.NONE:
    case undefined:
      return SORT_DIRECTIONS.ASC;
    case SORT_DIRECTIONS.ASC:
      return SORT_DIRECTIONS.DESC;
    case SORT_DIRECTIONS.DESC:
    default:
      return SORT_DIRECTIONS.NONE;
  }
}

/** Null-safe sort for the report table's sortable columns. */
function sortReportDocs(rows, column, direction, displayNameFor) {
  if (!column || !direction || direction === SORT_DIRECTIONS.NONE) return rows;
  const sign = direction === SORT_DIRECTIONS.DESC ? -1 : 1;

  const extractValue = (row) => {
    if (column === "title") return row.title;
    if (column === "state") return STATUS_LABELS[row.state] || row.state;
    if (column === "createdBy") return displayNameFor(row.createdBy);
    if (column === "createdDate") return row.createdDate;
    if (column === "lastModifiedDate") return row.lastModifiedDate;
    return undefined;
  };

  const isDateColumn = column === "createdDate" || column === "lastModifiedDate";

  return [...rows].sort((a, b) => {
    const va = extractValue(a);
    const vb = extractValue(b);
    const aNull = va === null || va === undefined || va === "";
    const bNull = vb === null || vb === undefined || vb === "";
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    if (isDateColumn) {
      return (new Date(va) - new Date(vb)) * sign;
    }
    return String(va).localeCompare(String(vb)) * sign;
  });
}

/** Sortable table for List view — Title/Status/Created/Last Modified/Actions. */
function ReportDocTable({ reportDocs, displayNameFor }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.NONE);

  const handleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection(SORT_DIRECTIONS.ASC);
    } else {
      setSortDirection(nextSortDirection(sortDirection));
    }
  };

  const sortedReportDocs = useMemo(
    () => sortReportDocs(reportDocs, sortColumn, sortDirection, displayNameFor),
    [reportDocs, sortColumn, sortDirection, displayNameFor]
  );

  const sortIcon = (column) => {
    if (sortColumn !== column) return <FaSort />;
    if (sortDirection === SORT_DIRECTIONS.ASC) return <FaSortUp />;
    if (sortDirection === SORT_DIRECTIONS.DESC) return <FaSortDown />;
    return <FaSort />;
  };

  const SortableTh = ({ column, children }) => (
    <Th>
      <Flex align="center" gap={1} onClick={() => handleSort(column)} cursor="pointer">
        <Box>{children}</Box>
        <Box>{sortIcon(column)}</Box>
      </Flex>
    </Th>
  );

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <SortableTh column="title">Title</SortableTh>
            <SortableTh column="state">Status</SortableTh>
            <SortableTh column="createdBy">Created By</SortableTh>
            <SortableTh column="createdDate">Created On</SortableTh>
            <SortableTh column="lastModifiedDate">Last Modified</SortableTh>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedReportDocs.map((reportDoc) => (
            <ReportDocTableRow key={reportDoc.id} reportDoc={reportDoc} createdByName={displayNameFor(reportDoc.createdBy)} />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function ReportDocTableRow({ reportDoc, createdByName }) {
  const { task, thread } = useReportDocProgress(reportDoc);
  const href = routes.reportDocEditor.replace(":id", reportDoc.id);

  return (
    <Tr _hover={{ bg: "gray.50" }}>
      <Td>
        <A href={href}>
          <Text fontWeight="600" color="darkTeal" noOfLines={1}>
            {reportDoc.title}
          </Text>
        </A>
      </Td>
      <Td>
        <ReportDocStateToggle reportDoc={reportDoc} task={task} thread={thread} />
      </Td>
      <Td fontSize="sm" color="gray.700">
        {createdByName}
      </Td>
      <Td fontSize="sm" color="gray.700">
        {formatDate(reportDoc.createdDate)}
      </Td>
      <Td fontSize="sm" color="gray.700">
        {formatDate(reportDoc.lastModifiedDate)}
      </Td>
      <Td>
        <ReportDocActionsMenu reportDoc={reportDoc} href={href} />
      </Td>
    </Tr>
  );
}

export default WithAuthorization(ReportDocListPage);
